import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Trash2, Edit2, Users as UsersIcon, Download, Upload, Search, Shield, Wrench, Eye, Filter } from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { Card, Button, Input, Modal, Badge, Select } from '../components/ui';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = [
  { value: 'Production', label: 'Production', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'Quality', label: 'Quality Control', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'Supervisor', label: 'Supervisor', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { value: 'Maintenance', label: 'Maintenance', color: 'bg-amber-100 text-amber-700 border-amber-200' },
];

function getDeptColor(dept) {
  return DEPARTMENTS.find(d => d.value === dept)?.color || 'bg-slate-100 text-slate-700 border-slate-200';
}

function sortByEmployeeId(a, b) {
  const numA = parseInt((a.employee_id || '').replace(/\D/g, '')) || 9999;
  const numB = parseInt((b.employee_id || '').replace(/\D/g, '')) || 9999;
  return numA - numB;
}

export default function Personnel() {
  const { personnel, fetchPersonnel, createPersonnel, updatePersonnel, deletePersonnel } = useAppStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [formData, setFormData] = useState({ name: '', employee_id: '', department: 'Production' });
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { fetchPersonnel(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingPerson) {
      await updatePersonnel(editingPerson.id, formData);
      toast.success('Personnel updated');
    } else {
      await createPersonnel(formData);
      toast.success('Personnel added');
    }
    setIsModalOpen(false);
    resetForm();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this person? They will be unassigned from all stations.')) {
      await deletePersonnel(id);
      toast.success('Personnel deleted');
    }
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setFormData({ name: person.name, employee_id: person.employee_id, department: person.department });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingPerson(null);
    setFormData({ name: '', employee_id: '', department: 'Production' });
  };

  const handleExport = () => {
    const data = personnel.sort(sortByEmployeeId).map(p => ({
      employee_id: p.employee_id, name: p.name, department: p.department,
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `personnel_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} personnel`);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) { toast.error('Invalid format: expected array'); return; }
      let added = 0, updated = 0;
      for (const p of data) {
        if (!p.name || !p.employee_id) continue;
        const existing = personnel.find(e => e.employee_id === p.employee_id);
        if (existing) {
          await updatePersonnel(existing.id, { name: p.name, employee_id: p.employee_id, department: p.department || 'Production' });
          updated++;
        } else {
          await createPersonnel({ name: p.name, employee_id: p.employee_id, department: p.department || 'Production' });
          added++;
        }
      }
      toast.success(`Import complete: ${added} added, ${updated} updated`);
    } catch (err) {
      toast.error('Import failed: ' + err.message);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // Filter & sort
  const filtered = personnel
    .filter(p => filter === 'All' || p.department === filter)
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.employee_id.toLowerCase().includes(search.toLowerCase()))
    .sort(sortByEmployeeId);

  // Stats
  const deptCounts = {};
  personnel.forEach(p => { deptCounts[p.department] = (deptCounts[p.department] || 0) + 1; });

  return (
    <div className="space-y-5 animate-fade-in" data-testid="personnel-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Personnel Management</h1>
          <p className="text-slate-500 mt-0.5 text-sm">Manage workers, departments, and assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handleExport} data-testid="export-personnel-btn">
            <Download className="w-4 h-4" /> Export
          </Button>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors">
            <Upload className="w-4 h-4" /> Import
            <input ref={fileRef} type="file" accept=".json" onChange={handleImport} className="hidden" data-testid="import-personnel-input" />
          </label>
          <Button onClick={() => setIsModalOpen(true)} data-testid="add-personnel-btn">
            <UserPlus className="w-4 h-4" /> Add Personnel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-3">
        <button onClick={() => setFilter('All')} className={`p-3 rounded-lg border text-left transition-all ${filter === 'All' ? 'border-slate-800 bg-slate-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
          <p className="text-2xl font-bold text-slate-900">{personnel.length}</p>
          <p className="text-[10px] font-semibold text-slate-500 uppercase">All Personnel</p>
        </button>
        {DEPARTMENTS.map(d => (
          <button key={d.value} onClick={() => setFilter(f => f === d.value ? 'All' : d.value)}
            className={`p-3 rounded-lg border text-left transition-all ${filter === d.value ? 'border-slate-800 bg-slate-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <p className="text-2xl font-bold text-slate-900">{deptCounts[d.value] || 0}</p>
            <p className="text-[10px] font-semibold text-slate-500 uppercase">{d.label}</p>
          </button>
        ))}
      </div>

      {/* Search + Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or ID..."
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            data-testid="personnel-search" />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} of {personnel.length}</span>
      </div>

      {/* Personnel Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase w-24">ID</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">Full Name</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase w-36">Department</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`} data-testid={`personnel-row-${p.employee_id}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-600 font-semibold">{p.employee_id}</td>
                <td className="px-4 py-2 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getDeptColor(p.department)}`}>
                    {p.department}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => handleEdit(p)} className="p-1 text-slate-400 hover:text-blue-600 transition-colors" data-testid={`edit-btn-${p.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="p-1 text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-btn-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center text-slate-400 py-8 text-sm">
            {search ? 'No results found' : 'No personnel in this category'}
          </p>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingPerson ? 'Edit Personnel' : 'Add Personnel'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Full Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., John Smith" required data-testid="personnel-name-input" />
          <Input label="Employee ID" value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
            placeholder="e.g., EMP001" required data-testid="personnel-id-input" />
          <Select label="Department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            options={DEPARTMENTS.map(d => ({ value: d.value, label: d.label }))} data-testid="personnel-dept-select" />
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setIsModalOpen(false); resetForm(); }}>Cancel</Button>
            <Button type="submit" data-testid="personnel-submit-btn">{editingPerson ? 'Update' : 'Add'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
